import { useMemo, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { StarFill, Star, CheckCircleFill } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';

import { submitSurvey } from '../../api/survey';

const SURVEY_COOKIE = 'bk_survey_submitted';

// Cookie is scoped per user so that different users logging in on the same
// browser each get their own survey state.
export const getSurveyCookieName = (userId) =>
  userId ? `${SURVEY_COOKIE}_${userId}` : SURVEY_COOKIE;

// Only these users should ever see the survey modal.
const ALLOWED_USERNAMES = [
  'GUARDA_L',
  'SO_JG',
  'PARIAL_LC',
  'ROSILLO_R',
  'BAUTISTA_A',
  'ESPIRITU_GK',
  'GUARDA_LO',
  'ESPIRITU_GI',
  'guarda_lor',
  'saludes_l',
  'Florentino_H',
  'CATIBOG_V',
  'RAQUIZA_G',
  'LOPEZ_RO',
  'vicente_kk',
  'DEGUZMAN_D',
  'SEMILLA_L',
].map((username) => username.toLowerCase());

const SURVEY_ITEMS = [
  {
    key: 'overall_satisfaction',
    title: '1. Overall Satisfaction',
    description:
      'How satisfied are you with the overall implementation of the new features and adjustments?',
  },
  {
    key: 'support_communication',
    title:
      '2. Support and Communication: During and After Implementation of Features and Adjustments',
    description:
      'How would you rate the communication and updates provided during and after the system implementation phase?',
  },
  {
    key: 'process_efficiency',
    title: '3. Process Efficiency',
    description:
      'How have the new features and adjustments impacted the efficiency of your processes?',
  },
  {
    key: 'accuracy_reliability',
    title: '4. Accuracy and Reliability',
    description:
      'Are the results, data, or system behavior more accurate or reliable after the enhancement?',
  },
  {
    key: 'resolution_of_issues',
    title: '5. Resolution of Issues',
    description:
      'How satisfied are you with how quickly issues or technical problems were addressed after the features and adjustments were implemented?',
  },
];

const buildInitialAnswers = () =>
  SURVEY_ITEMS.reduce((acc, item) => {
    acc[item.key] = { rating: 0, reason: '' };
    return acc;
  }, {});

const ANIMATION_STYLES = `
  @keyframes starPop {
    0%   { transform: scale(1); }
    40%  { transform: scale(1.5); }
    70%  { transform: scale(0.9); }
    100% { transform: scale(1); }
  }
  @keyframes checkIn {
    0%   { transform: scale(0); opacity: 0; }
    60%  { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes fadeUp {
    0%   { transform: translateY(16px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  .survey-star-pop {
    animation: starPop 0.35s ease forwards;
  }
`;

const StarRating = ({
  itemKey,
  value,
  onChange,
  animatingStar,
  onStarAnimationEnd,
}) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="d-flex align-items-center" style={{ gap: '4px' }}>
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= (hover || value);
        const Icon = active ? StarFill : Star;
        const isAnimating =
          animatingStar?.key === itemKey && animatingStar?.star === star;
        return (
          <span
            key={star}
            className={isAnimating ? 'survey-star-pop' : ''}
            style={{ display: 'inline-block', lineHeight: 0 }}
            onAnimationEnd={isAnimating ? onStarAnimationEnd : undefined}
          >
            <Icon
              size={26}
              color={active ? '#f5b301' : '#c4c4c4'}
              style={{ cursor: 'pointer', display: 'block' }}
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
            />
          </span>
        );
      })}
    </div>
  );
};

const SurveyModal = ({ show, onClose }) => {
  const { profileData } = useSelector(({ auth }) => auth);

  const isAllowedUser = useMemo(
    () =>
      ALLOWED_USERNAMES.includes((profileData?.username || '').toLowerCase()),
    [profileData],
  );

  const [answers, setAnswers] = useState(buildInitialAnswers);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState('form'); // 'form' | 'animating' | 'thankyou'
  const [animatingStar, setAnimatingStar] = useState(null); // { key, star }

  const isComplete = useMemo(
    () =>
      SURVEY_ITEMS.every(
        ({ key }) =>
          answers[key].rating >= 1 && answers[key].reason.trim().length > 0,
      ),
    [answers],
  );

  const setRating = (key, rating) => {
    setAnimatingStar({ key, star: rating });
    setAnswers((prev) => ({ ...prev, [key]: { ...prev[key], rating } }));
  };

  const setReason = (key, reason) =>
    setAnswers((prev) => ({ ...prev, [key]: { ...prev[key], reason } }));

  const buildPayload = () => {
    const name = `${profileData?.first_name || ''} ${
      profileData?.last_name || ''
    }`.trim();

    const payload = {
      source: 'ERP',
      name,
      email: profileData?.email || '',
      submitted_at: moment().format('YYYY-MM-DD HH:mm:ss'),
    };

    SURVEY_ITEMS.forEach(({ key }) => {
      payload[`${key}_rating`] = answers[key].rating;
      payload[`${key}_reason`] = answers[key].reason.trim();
    });

    return payload;
  };

  const handleSubmit = async () => {
    if (!isComplete || submitting) return;

    setSubmitting(true);
    try {
      await submitSurvey(buildPayload());
      Cookies.set(getSurveyCookieName(profileData?.user_id), '1', {
        path: '/',
        expires: 1,
      });
      setPhase('animating');
      setTimeout(() => setPhase('thankyou'), 1800);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Submission failed',
        text:
          error?.message ||
          'We could not submit your feedback. Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (phase === 'animating') {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ padding: '60px 0' }}
        >
          <CheckCircleFill
            size={96}
            color="#28a745"
            style={{
              animation:
                'checkIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
            }}
          />
        </div>
      );
    }

    if (phase === 'thankyou') {
      return (
        <div
          className="text-center"
          style={{ padding: '24px 0', animation: 'fadeUp 0.5s ease forwards' }}
        >
          <h5 className="mb-2">Thank you!</h5>
          <p className="text-muted mb-0">
            Your feedback has been submitted successfully.
          </p>
        </div>
      );
    }

    return (
      <>
        <Alert variant="info">
          Thank you for being a part of our recent ERP feature implementation
          and adjustments the last quarter <strong>(April–May)</strong>. We hope
          the transition was smooth and beneficial for your work.
          <br />
          <br />
          We value your feedback and would love to hear about your experience
          with the new features and adjustments. Your insights will help us
          refine the implementation process and improve future updates. Please
          take a moment to complete this brief survey (your responses will
          remain anonymous). Thank you
        </Alert>

        {SURVEY_ITEMS.map((item) => (
          <div key={item.key} className="mb-4">
            <h6 className="mb-1">{item.title}</h6>
            <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
              {item.description}
            </p>

            <StarRating
              itemKey={item.key}
              value={answers[item.key].rating}
              onChange={(rating) => setRating(item.key, rating)}
              animatingStar={animatingStar}
              onStarAnimationEnd={() => setAnimatingStar(null)}
            />

            <Form.Control
              as="textarea"
              rows={2}
              className="mt-2"
              placeholder="Reason for your rating"
              value={answers[item.key].reason}
              onChange={(e) => setReason(item.key, e.target.value)}
            />
          </div>
        ))}
      </>
    );
  };

  const renderFooter = () => {
    if (phase === 'thankyou') {
      return (
        <Modal.Footer>
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      );
    }

    if (phase === 'form') {
      return (
        <Modal.Footer>
          <Button
            variant="success"
            onClick={handleSubmit}
            disabled={!isComplete || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Modal.Footer>
      );
    }

    return null;
  };

  if (!isAllowedUser) return null;

  // Survey period has ended — modal is disabled.
  return null;

  return (
    <>
      <style>{ANIMATION_STYLES}</style>
      <Modal
        show={show}
        onHide={phase === 'thankyou' ? onClose : () => {}}
        backdrop="static"
        size="lg"
        centered
        scrollable
      >
        <Modal.Header>
          <Modal.Title>Customer Satisfaction Survey</Modal.Title>
        </Modal.Header>

        <Modal.Body>{renderBody()}</Modal.Body>

        {renderFooter()}
      </Modal>
    </>
  );
};

export default SurveyModal;
